// Champagne Wine Shop — delivery picker for the cart (Horizon theme).
// Writes the customer's choice to hidden line item properties (_cw_ship_*).
// The cw-delivery Worker reads them and shows matching options at checkout.

export const P = {
  MODE: '_cw_ship_mode',
  CARRIER: '_cw_ship_carrier',
  OFFICE_ID: '_cw_ship_office_id',
  OFFICE_NAME: '_cw_ship_office_name',
  OFFICE_ADDRESS: '_cw_ship_office_address',
};

const STORE_KEY = 'cw_delivery_choice';
const LOCKER = { econt: 'До Еконтомат', speedy: 'До автомат' };

export function propsFor(state) {
  const office = state.mode !== 'address' && state.office ? state.office : null;
  return {
    [P.MODE]: state.mode,
    [P.CARRIER]: state.carrier,
    [P.OFFICE_ID]: office ? String(office.id) : '',
    [P.OFFICE_NAME]: office ? office.name : '',
    [P.OFFICE_ADDRESS]: office ? [office.city, office.address].filter(Boolean).join(', ') : '',
  };
}

export function lineNeedsUpdate(line, wanted) {
  const p = line.properties || {};
  return Object.keys(wanted).some((k) => (p[k] ?? '') !== wanted[k]);
}

export function stateFromCart(cart) {
  const line = (cart.items || []).find((l) => l.properties && l.properties[P.MODE]);
  if (!line) return null;
  const p = line.properties;
  const state = { mode: p[P.MODE], carrier: p[P.CARRIER] || 'econt', office: null };
  if (p[P.OFFICE_ID]) {
    state.office = { id: p[P.OFFICE_ID], name: p[P.OFFICE_NAME] || '', address: p[P.OFFICE_ADDRESS] || '', city: '' };
  }
  return state;
}

export function isComplete(state) {
  return state.mode === 'address' || Boolean(state.office && state.office.id);
}

const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

async function getCart() {
  const r = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
  return r.json();
}

// Rewrites properties line by line. Keys change after each update, so the
// cart is re-read every time. Bounded so it can never loop forever.
export async function syncCart(wanted, { fetchCart = getCart, change } = {}) {
  const doChange = change || (async (body) => {
    const r = await fetch(`${root()}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!r.ok) throw new Error(`cart/change ${r.status}`);
    return r.json();
  });
  let cart = await fetchCart();
  for (let guard = 0; guard < 60; guard++) {
    const line = (cart.items || []).find((l) => lineNeedsUpdate(l, wanted));
    if (!line) return cart;
    const body = { id: line.key, quantity: line.quantity, properties: { ...(line.properties || {}), ...wanted } };
    if (line.selling_plan_allocation) body.selling_plan = line.selling_plan_allocation.selling_plan.id;
    cart = await doChange(body);
  }
  throw new Error('cart sync did not settle');
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// One shared choice for the whole page: the cart page and the cart drawer can
// both show a picker, and they must never disagree or race each other.
const shared = {
  state: { mode: 'address', carrier: 'econt', office: null },
  pending: 0,
  chain: Promise.resolve(),
  passing: false,
  restoring: null,
  wired: false,
  touched: false,
};

const instances = new Set();
const renderAll = () => instances.forEach((el) => el.render());
const isBusy = () => shared.pending > 0;

function setState(next) {
  shared.touched = true;
  shared.state = next;
  try { localStorage.setItem(STORE_KEY, JSON.stringify(next)); } catch { /* private mode */ }
  renderAll();
}

// Every sync is chained; the busy flag stays on until the LAST queued sync ends.
export function queueSync() {
  // An unfinished office choice is synced too (empty office id): the Worker then
  // offers address delivery only, so no stale office from an earlier pick survives.
  shared.pending++;
  renderAll();
  shared.chain = shared.chain
    .then(() => syncCart(propsFor(shared.state))) // reads the latest state when it runs
    .then(() => instances.forEach((el) => el.showError('')))
    .catch(() => instances.forEach((el) => el.showError('Изборът не се записа. Опреснете страницата и опитайте отново.')))
    .finally(() => { shared.pending--; renderAll(); });
  return shared.chain;
}

async function restoreOnce() {
  if (!shared.restoring) {
    shared.restoring = (async () => {
      try {
        const fromCart = stateFromCart(await getCart());
        let saved = null;
        try { saved = JSON.parse(localStorage.getItem(STORE_KEY) || 'null'); } catch { saved = null; }
        const next = fromCart || saved || shared.state;
        if (fromCart && saved && saved.office && fromCart.office && saved.office.id === fromCart.office.id) {
          next.office = saved.office; // richer copy with city/address
        }
        if (!shared.touched) shared.state = next; // the customer's click wins over a restore
      } catch { /* keep defaults */ }
      renderAll();
      queueSync();
    })();
  }
  return shared.restoring;
}

function wireDocumentOnce() {
  if (shared.wired) return;
  shared.wired = true;
  // Block checkout until the choice is complete and saved to the cart.
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest && e.target.closest('#checkout, button[name="checkout"]');
    if (!btn || shared.passing || !instances.size) return;
    if (!isComplete(shared.state)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      const msg = shared.state.mode === 'locker' ? 'Изберете автомат, от който да вземете пратката.' : 'Изберете офис, от който да вземете пратката.';
      instances.forEach((el) => el.showError(msg));
      const visible = [...instances].find((el) => el.offsetParent !== null) || [...instances][0];
      visible.focusSearch();
      return;
    }
    if (isBusy()) {
      e.preventDefault();
      e.stopImmediatePropagation();
      while (isBusy()) await shared.chain;
      shared.passing = true;
      try { btn.click(); } finally { shared.passing = false; }
    }
  }, true);
  // Items added later need the properties too; push the one shared choice.
  document.addEventListener('cart:update', () => queueSync());
}

let uid = 0;

function definePicker() {
class CwDeliveryPicker extends HTMLElement {
  connectedCallback() {
    instances.add(this);
    if (this._ready) { this.render(); return; }
    this._ready = true;
    this.api = (this.dataset.api || '').replace(/\/$/, '');
    this.$ = (s) => this.querySelector(s);

    // Unique ids/names per instance (page + drawer can coexist).
    const n = ++uid;
    const q = this.$('[data-cw-q]');
    q.id = `cw-dp-q-${n}`;
    const lbl = this.querySelector('label[for="cw-dp-q"]');
    if (lbl) lbl.htmlFor = q.id;
    this.querySelectorAll('input[name="cw_carrier"]').forEach((i) => { i.name = `cw_carrier_${n}`; i.dataset.kind = 'carrier'; });
    this.querySelectorAll('input[name="cw_mode"]').forEach((i) => { i.name = `cw_mode_${n}`; i.dataset.kind = 'mode'; });

    this.addEventListener('change', (e) => this.onChange(e));
    q.addEventListener('input', () => this.onSearch());
    this.$('[data-cw-change]').addEventListener('click', () => {
      setState({ ...shared.state, office: null });
      this.focusSearch();
    });
    this.$('[data-cw-results]').addEventListener('click', (e) => this.onPick(e));

    wireDocumentOnce();
    this.render();
    restoreOnce();
  }

  disconnectedCallback() {
    instances.delete(this);
  }

  focusSearch() {
    const q = this.$('[data-cw-q]');
    if (q && !q.closest('[hidden]')) q.focus();
  }

  onChange(e) {
    const t = e.target;
    const s = shared.state;
    if (t.dataset.kind === 'carrier' && t.value !== s.carrier) {
      setState({ ...s, carrier: t.value, office: null }); // offices belong to one courier
    } else if (t.dataset.kind === 'mode' && t.value !== s.mode) {
      setState({ ...s, mode: t.value, office: null });
    } else {
      return;
    }
    if (shared.state.mode !== 'address') this.onSearch();
    queueSync();
  }

  onSearch() {
    clearTimeout(this._t);
    this._t = setTimeout(() => this.search(), 250);
  }

  async search() {
    const s = shared.state;
    if (s.mode === 'address' || s.office) return;
    const q = this.$('[data-cw-q]').value.trim();
    const list = this.$('[data-cw-results]');
    if (q.length < 2) { list.innerHTML = ''; return; }
    const url = `${this.api}/offices?carrier=${s.carrier}&type=${s.mode}&q=${encodeURIComponent(q)}`;
    const seq = (this._seq = (this._seq || 0) + 1);
    try {
      const data = await (await fetch(url)).json();
      if (seq !== this._seq) return;
      this._results = data.offices || [];
      list.innerHTML = this._results.length
        ? this._results.map((o, i) => `<li><button type="button" role="option" data-i="${i}">${escapeHtml(o.name)}<small>${escapeHtml([o.city, o.address].filter(Boolean).join(', '))}</small></button></li>`).join('')
        : '<li><small>Няма намерени. Опитайте с името на града.</small></li>';
    } catch {
      list.innerHTML = '<li><small>Списъкът не се зареди. Опитайте отново.</small></li>';
    }
  }

  onPick(e) {
    const btn = e.target.closest('button[data-i]');
    if (!btn) return;
    const o = this._results[Number(btn.dataset.i)];
    // Change the DOM only after this click has finished bubbling. The theme's
    // drawer treats a click whose target is no longer inside it as a click on
    // the backdrop and closes itself (seen on mobile, where it is modal).
    setTimeout(() => {
      this.$('[data-cw-results]').innerHTML = '';
      this.$('[data-cw-q]').value = '';
      setState({ ...shared.state, office: { id: o.id, name: o.name, city: o.city, address: o.address } });
      queueSync();
    }, 0);
  }

  showError(msg) {
    const el = this.$('[data-cw-error]');
    el.textContent = msg;
    el.hidden = !msg;
  }

  render() {
    if (!this.$) return;
    const s = shared.state;
    this.querySelectorAll('input[data-kind="carrier"]').forEach((i) => { i.checked = i.value === s.carrier; });
    this.querySelectorAll('input[data-kind="mode"]').forEach((i) => { i.checked = i.value === s.mode; });
    this.$('[data-cw-locker-label]').textContent = LOCKER[s.carrier];

    const officeMode = s.mode !== 'address';
    this.$('[data-cw-office]').hidden = !officeMode;
    this.$('[data-cw-chosen]').hidden = !s.office;
    this.$('[data-cw-search]').hidden = Boolean(s.office);
    if (s.office) {
      this.$('[data-cw-chosen-name]').textContent = s.office.name;
      this.$('[data-cw-chosen-address]').textContent = [s.office.city, s.office.address].filter(Boolean).join(', ');
    } else if (!officeMode) {
      this.$('[data-cw-results]').innerHTML = '';
    }

    const courier = s.carrier === 'econt' ? 'Еконт' : 'Спиди';
    let note;
    if (!officeMode) note = `${courier} ще донесе пратката до адреса, който ще въведете на следващата стъпка.`;
    else if (!s.office) note = s.mode === 'locker' ? 'Потърсете автомат по град или име.' : 'Потърсете офис по град или име.';
    else note = `Ще вземете пратката сами от избрания ${s.mode === 'locker' ? 'автомат' : 'офис'}. Тя няма да бъде доставена до адреса ви.`;
    this.$('[data-cw-note]').textContent = note;
    if (isBusy()) this.setAttribute('aria-busy', 'true'); else this.removeAttribute('aria-busy');
    if (isComplete(s) && !this.$('[data-cw-error]').hidden && /Изберете/.test(this.$('[data-cw-error]').textContent)) this.showError('');
  }
}

if (!customElements.get('cw-delivery-picker')) customElements.define('cw-delivery-picker', CwDeliveryPicker);
}

if (typeof customElements !== 'undefined') definePicker();
